import { useState, useRef } from 'react';
import { Button } from '../ui/button';
import { Camera, Upload, X, Image, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';

/**
 * PAN Photo Upload Component
 * 
 * Features:
 * - Camera capture option (mobile/desktop with camera)
 * - File upload option (gallery/file picker)
 * - Image compression before upload (max 200KB)
 * - Preview before submit
 * - Upload progress indicator
 */
export const PANPhotoUpload = ({ 
  onPhotoCapture, 
  existingPhoto = null,
  disabled = false,
  maxSizeKB = 200 
}) => {
  const [preview, setPreview] = useState(existingPhoto);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  // Compress image to target size
  const compressImage = async (base64Data, maxKB = maxSizeKB) => {
    return new Promise((resolve, reject) => {
      const img = document.createElement('img');
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        // Calculate size reduction ratio
        const maxDimension = 1200; // Max width/height
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = (height / width) * maxDimension;
            width = maxDimension;
          } else {
            width = (width / height) * maxDimension;
            height = maxDimension;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Try different quality levels to meet size target
        let quality = 0.8;
        let result = canvas.toDataURL('image/jpeg', quality);
        
        while (result.length > maxKB * 1024 * 1.37 && quality > 0.1) {
          quality -= 0.1;
          result = canvas.toDataURL('image/jpeg', quality);
        }
        
        // If still too large, reduce dimensions further
        if (result.length > maxKB * 1024 * 1.37) {
          width *= 0.7;
          height *= 0.7;
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          result = canvas.toDataURL('image/jpeg', 0.7);
        }
        
        resolve(result);
      };
      img.onerror = reject;
      img.src = base64Data;
    });
  };

  // Handle file selection from input
  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate initial file size (max 10MB before compression)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large. Please select an image under 10MB');
      return;
    }

    setIsProcessing(true);

    try {
      // Read file as base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64Data = e.target.result;
          
          // Compress image
          const compressed = await compressImage(base64Data);
          
          // Extract base64 data without prefix for API
          const base64ForAPI = compressed.split(',')[1];
          
          setPreview(compressed);
          onPhotoCapture(base64ForAPI);
          toast.success('Photo added successfully');
        } catch (err) {
          console.error('Compression error:', err);
          toast.error('Failed to process image');
        } finally {
          setIsProcessing(false);
        }
      };
      reader.onerror = () => {
        toast.error('Failed to read file');
        setIsProcessing(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('File processing error:', err);
      toast.error('Failed to process file');
      setIsProcessing(false);
    }

    // Reset input
    event.target.value = '';
  };

  // Start camera capture
  const startCamera = async () => {
    try {
      setIsCapturing(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }, // Prefer back camera
        audio: false
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('Camera access error:', err);
      toast.error('Could not access camera. Please use file upload instead.');
      setIsCapturing(false);
    }
  };

  // Capture photo from camera
  const capturePhoto = async () => {
    if (!videoRef.current) return;

    setIsProcessing(true);

    try {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoRef.current, 0, 0);
      
      const base64Data = canvas.toDataURL('image/jpeg', 0.9);
      
      // Compress if needed
      const compressed = await compressImage(base64Data);
      const base64ForAPI = compressed.split(',')[1];
      
      setPreview(compressed);
      onPhotoCapture(base64ForAPI);
      toast.success('Photo captured successfully');
      
      // Stop camera
      stopCamera();
    } catch (err) {
      console.error('Capture error:', err);
      toast.error('Failed to capture photo');
    } finally {
      setIsProcessing(false);
    }
  };

  // Stop camera stream
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsCapturing(false);
  };

  // Clear current photo
  const clearPhoto = () => {
    setPreview(null);
    onPhotoCapture(null);
  };

  return (
    <div className="space-y-4">
      {/* Camera View */}
      {isCapturing && (
        <div className="relative rounded-lg overflow-hidden bg-black">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            className="w-full max-h-64 object-cover"
          />
          <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-4">
            <Button
              type="button"
              onClick={capturePhoto}
              disabled={isProcessing}
              className="bg-white text-black hover:bg-gray-100 rounded-full w-16 h-16"
            >
              {isProcessing ? (
                <Loader2 className="w-8 h-8 animate-spin" />
              ) : (
                <Camera className="w-8 h-8" />
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={stopCamera}
              className="bg-red-500 text-white hover:bg-red-600 border-none"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
      )}

      {/* Preview */}
      {preview && !isCapturing && (
        <div className="relative rounded-lg overflow-hidden border-2 border-green-500 bg-gray-50">
          <img
            src={preview}
            alt="PAN Card Preview"
            className="w-full max-h-64 object-contain"
          />
          <div className="absolute top-2 right-2 flex gap-2">
            <div className="bg-green-500 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
              <Check className="w-3 h-3" />
              Ready
            </div>
            {!disabled && (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={clearPhoto}
                className="rounded-full w-8 h-8 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Upload Options */}
      {!preview && !isCapturing && (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-gray-50">
          <Image className="w-12 h-12 mx-auto text-gray-400 mb-3" />
          <p className="text-sm text-gray-600 mb-4">
            Upload a clear photo of your PAN card
          </p>
          
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            {/* File Upload Button */}
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || isProcessing}
              className="flex items-center gap-2"
              data-testid="pan-upload-btn"
            >
              {isProcessing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              Upload Photo
            </Button>
            
            {/* Camera Button */}
            <Button
              type="button"
              variant="outline"
              onClick={startCamera}
              disabled={disabled || isProcessing}
              className="flex items-center gap-2"
              data-testid="pan-camera-btn"
            >
              <Camera className="w-4 h-4" />
              Take Photo
            </Button>
          </div>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          <p className="text-xs text-gray-500 mt-3">
            Supported formats: JPEG, PNG (Max 10MB, will be compressed)
          </p>
        </div>
      )}

      {/* Processing Indicator */}
      {isProcessing && !isCapturing && (
        <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
          <Loader2 className="w-4 h-4 animate-spin" />
          Processing image...
        </div>
      )}
    </div>
  );
};

export default PANPhotoUpload;
